import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Select, Tooltip, Input, Badge } from 'antd';
import {
    ApiOutlined,
    DisconnectOutlined,
    ClearOutlined,
    VerticalAlignBottomOutlined,
    SendOutlined,
} from '@ant-design/icons';
import { ARDUINO_USB_FILTERS } from "../../services/arduino-service";

const BAUD_RATES = [300, 1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200, 230400, 250000, 500000, 1000000, 2000000];

const LINE_ENDINGS = [
    { label: 'No line ending', value: '' },
    { label: 'Newline (\\n)', value: '\n' },
    { label: 'Carriage return (\\r)', value: '\r' },
    { label: 'Both NL & CR (\\r\\n)', value: '\r\n' },
];

/**
 * SerialMonitor
 *
 * Props:
 *  - isUploadInProgress: bool   → when true, the monitor disconnects automatically
 *                                  so the upload can own the port without contention.
 *  - onConnectionChange: fn(isConnected: bool) → optional callback so the parent
 *                                  knows the current connection state.
 */
const SerialMonitor = ({ isUploadInProgress, onConnectionChange }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [output, setOutput] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [baudRate, setBaudRate] = useState(9600);
    const [lineEnding, setLineEnding] = useState('\n');
    const [autoScroll, setAutoScroll] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');

    // Refs that survive re-renders — used inside async callbacks
    const portRef = useRef(null);
    const writerRef = useRef(null);
    const readerRef = useRef(null);
    const activeRef = useRef(false); // stops the read loop cleanly
    const outputRef = useRef(null);  // for auto-scroll

    // ── Buffer & Throttling for High-Speed Data ──────────────────────────────
    const bufferRef = useRef('');
    const flushTimeoutRef = useRef(null);

    const flushBufferToState = useCallback(() => {
        // Snapshot and clear the buffer BEFORE the async setState call.
        // This prevents a race condition where new data arriving during
        // React's async batching would be wiped by the deferred clear.
        const snapshot = bufferRef.current;
        if (!snapshot) return;

        bufferRef.current = '';
        flushTimeoutRef.current = null;

        setOutput((prev) => {
            let newOutput = prev + snapshot;
            // Limit to ~10000 chars to avoid memory leaks & UI freezes
            if (newOutput.length > 10000) {
                newOutput = newOutput.slice(-10000);
            }
            return newOutput;
        });
    }, []);

    const queueOutput = (text) => {
        bufferRef.current += text;
        if (!flushTimeoutRef.current) {
            // Throttle UI updates to ~15fps for butter-smooth performance
            flushTimeoutRef.current = setTimeout(flushBufferToState, 60);
        }
    };

    // ── Auto-scroll ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (autoScroll && outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [output, autoScroll]);

    // ── Disconnect when upload starts (port contention prevention) ───────────
    useEffect(() => {
        if (isUploadInProgress && isConnected) {
            setStatusMessage('⚠ Upload started — disconnecting serial monitor to release port…');
            internalDisconnect();
        }
    }, [isUploadInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Clean up on unmount ──────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            internalDisconnect();
            if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ────────────────────────────────────────────────────────────────────────
    //  Core disconnect logic (safe to call multiple times)
    // ────────────────────────────────────────────────────────────────────────
    const internalDisconnect = async () => {
        activeRef.current = false;

        // 1. Cancel the reader (stops the read loop) then release its lock
        try {
            if (readerRef.current) {
                await readerRef.current.cancel();
                readerRef.current.releaseLock();
                readerRef.current = null;
            }
        } catch (_) { }

        // 2. Release writer lock
        try {
            if (writerRef.current) {
                writerRef.current.releaseLock();
                writerRef.current = null;
            }
        } catch (_) { }

        // 3. Close the port
        try {
            if (portRef.current) {
                await portRef.current.close();
                portRef.current = null;
            }
        } catch (_) { }

        setIsConnected(false);
        if (onConnectionChange) onConnectionChange(false);
    };

    const disconnect = useCallback(async () => {
        setStatusMessage('Disconnecting…');
        await internalDisconnect();
        setStatusMessage('Disconnected.');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ────────────────────────────────────────────────────────────────────────
    //  Connect
    // ────────────────────────────────────────────────────────────────────────
    const connect = async () => {
        if (!('serial' in navigator)) {
            setStatusMessage('✗ Web Serial API is not supported in this browser. Please use Chrome or Edge.');
            return;
        }
        if (isUploadInProgress) {
            setStatusMessage('⚠ Cannot open serial monitor while an upload is in progress.');
            return;
        }

        setIsConnecting(true);
        setStatusMessage('Connecting…');

        try {
            // Try previously-authorized ports first to avoid the browser popup
            const existingPorts = await navigator.serial.getPorts();
            let port;
            if (existingPorts.length > 0) {
                port = existingPorts[0];
                setStatusMessage('Found previously authorized port.');
            } else {
                port = await navigator.serial.requestPort({ filters: ARDUINO_USB_FILTERS });
            }

            await port.open({ baudRate });
            portRef.current = port;

            // Writer – for sending data to the board
            writerRef.current = port.writable.getWriter();

            // Read directly from port.readable using a plain reader.
            // Use TextDecoder with { stream: true } to correctly handle
            // multi-byte UTF-8 sequences split across chunks.
            const reader = port.readable.getReader();
            const decoder = new TextDecoder();
            readerRef.current = reader;

            activeRef.current = true;
            setIsConnected(true);
            setIsConnecting(false);
            setStatusMessage(`Connected at ${baudRate} baud.`);
            if (onConnectionChange) onConnectionChange(true);

            // ── Read loop ────────────────────────────────────────────────────
            (async () => {
                try {
                    while (activeRef.current) {
                        const { value, done } = await reader.read();
                        if (done || !activeRef.current) break;
                        // value is Uint8Array; decode with streaming mode so
                        // multi-byte chars split across packets are handled correctly
                        const text = decoder.decode(value, { stream: true });
                        queueOutput(text);
                    }
                    // Flush any remaining bytes after the stream ends
                    const remaining = decoder.decode();
                    if (remaining) queueOutput(remaining);
                } catch (err) {
                    if (activeRef.current) {
                        setStatusMessage('✗ Connection lost: ' + (err.message || 'port closed unexpectedly'));
                    }
                } finally {
                    setIsConnected(false);
                    if (onConnectionChange) onConnectionChange(false);
                }
            })();

        } catch (err) {
            setIsConnecting(false);
            const msg = err.name === 'NotFoundError'
                ? 'No port selected.'
                : '✗ ' + (err.message || 'Could not open port.');
            setStatusMessage(msg);
            // Clean up anything that was partially opened
            await internalDisconnect();
        }
    };

    // ────────────────────────────────────────────────────────────────────────
    //  Send data to board
    // ────────────────────────────────────────────────────────────────────────
    const sendData = async () => {
        if (!writerRef.current || !inputValue.trim()) return;
        try {
            const encoder = new TextEncoder();
            await writerRef.current.write(encoder.encode(inputValue + lineEnding));
            setInputValue('');
        } catch (err) {
            setStatusMessage('✗ Send failed: ' + err.message);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') sendData();
    };

    // ────────────────────────────────────────────────────────────────────────
    //  Render
    // ────────────────────────────────────────────────────────────────────────
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '590px',
            background: '#FFFFFF', // Standard white background
            borderRadius: '12px',
            overflow: 'hidden',
            fontFamily: "'Fredoka', 'Quicksand', 'Arial Rounded MT Bold', 'Comic Sans MS', sans-serif",
            border: '1px solid #D7BEF7', // Codebase purple border
            boxShadow: 'none',
        }}>

            {/* ── Toolbar ─────────────────────────────────────────────────── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 16px',
                background: '#FFF9F1', // Codebase light panel color
                borderBottom: '1px solid #D7BEF7',
                flexWrap: 'wrap',
            }}>
                {/* Status indicator */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#FFFFFF',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    border: '1px solid #D7BEF7'
                }}>
                    <Badge
                        color={isConnected ? '#2AC14E' : isConnecting ? '#FFC200' : '#E96358'}
                        style={{ marginRight: 6 }}
                    />
                    <span style={{ color: '#404040', fontSize: 13, fontWeight: 'bold' }}>
                        {isConnected ? 'Connected' : isConnecting ? 'Connecting…' : 'Disconnected'}
                    </span>
                </div>

                <div style={{ flex: 1 }} />

                {/* Baud rate */}
                <Select
                    size="middle"
                    value={baudRate}
                    onChange={setBaudRate}
                    disabled={isConnected || isConnecting}
                    popupMatchSelectWidth={false}
                    style={{ width: 130, fontWeight: 'bold' }}
                    options={BAUD_RATES.map(r => ({ label: `${r} baud`, value: r }))}
                />

                {/* Connect / Disconnect */}
                <Button
                    size="middle"
                    type="primary"
                    icon={isConnected ? <DisconnectOutlined /> : <ApiOutlined />}
                    loading={isConnecting}
                    onClick={isConnected ? disconnect : connect}
                    style={{
                        background: isConnected ? '#E96358' : '#00875A', // Codebase red / green
                        borderColor: isConnected ? '#E96358' : '#00875A',
                        fontWeight: 'bold',
                        borderRadius: '20px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                >
                    {isConnected ? 'Disconnect' : 'Connect'}
                </Button>

                {/* Auto-scroll toggle */}
                <Tooltip title={autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}>
                    <Button
                        size="middle"
                        shape="circle"
                        icon={<VerticalAlignBottomOutlined />}
                        onClick={() => setAutoScroll(p => !p)}
                        style={{
                            background: autoScroll ? '#FFF9F1' : '#FFFFFF',
                            borderColor: '#D7BEF7',
                            color: autoScroll ? '#7564C9' : '#404040',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                    />
                </Tooltip>

                {/* Clear */}
                <Tooltip title="Clear output">
                    <Button
                        size="middle"
                        shape="circle"
                        icon={<ClearOutlined />}
                        onClick={() => setOutput('')}
                        style={{
                            background: '#FFFFFF',
                            borderColor: '#D7BEF7',
                            color: '#E96358',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                    />
                </Tooltip>
            </div>

            {/* ── Status bar ──────────────────────────────────────────────── */}
            {statusMessage && (
                <div style={{
                    padding: '6px 16px',
                    background: statusMessage.startsWith('✗') ? '#FFE4E6' : statusMessage.startsWith('⚠') ? '#FFF9F1' : '#FFFFFF',
                    borderBottom: '1px solid #D7BEF7',
                    fontSize: 13,
                    fontWeight: '600',
                    color: statusMessage.startsWith('✗') ? '#E96358' : statusMessage.startsWith('⚠') ? '#FFC200' : '#4235A1',
                    fontFamily: 'inherit',
                }}>
                    {statusMessage}
                </div>
            )}

            {/* ── Output ──────────────────────────────────────────────────── */}
            <div
                ref={outputRef}
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '16px',
                    color: '#404040', // Standard codebase text color
                    fontSize: 15,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: '#FFFFFF',
                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                }}
            >
                {output
                    ? output
                    : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: '#404040',
                            fontFamily: "'Fredoka', 'Quicksand', sans-serif"
                        }}>
                            <ApiOutlined style={{ fontSize: '48px', marginBottom: '16px', color: '#D7BEF7' }} />
                            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#2E3876' }}>Serial output will appear here…</span>
                            <span style={{ fontSize: '14px', marginTop: '8px' }}>Connect your Arduino to get started!</span>
                        </div>
                    )
                }
            </div>

            {/* ── Input bar ───────────────────────────────────────────────── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: '#FFF9F1', // Match top toolbar
                borderTop: '1px solid #D7BEF7',
            }}>
                <Input
                    size="large"
                    placeholder={isConnected ? 'Type your message here…' : 'Connect your board to send a message!'}
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={!isConnected}
                    style={{
                        flex: 1,
                        background: '#FFFFFF',
                        border: '1px solid #D7BEF7',
                        color: '#404040',
                        borderRadius: '24px',
                        padding: '8px 16px',
                        fontSize: 15,
                        fontWeight: '500',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                    }}
                />

                {/* Line ending selector */}
                <Select
                    size="large"
                    value={lineEnding}
                    onChange={setLineEnding}
                    popupMatchSelectWidth={false}
                    style={{ width: 160, fontWeight: 'bold' }}
                    options={LINE_ENDINGS}
                />

                {/* Send */}
                <Button
                    size="large"
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={sendData}
                    disabled={!isConnected || !inputValue.trim()}
                    style={{
                        background: isConnected && inputValue.trim() ? '#4235A1' : '#D7BEF7', // Primary Save/Submit color
                        borderColor: isConnected && inputValue.trim() ? '#4235A1' : '#D7BEF7',
                        borderRadius: '24px',
                        fontWeight: 'bold',
                        padding: '0 24px',
                        boxShadow: isConnected && inputValue.trim() ? '0 4px 12px rgba(66, 53, 161, 0.2)' : 'none',
                        color: '#FFFFFF'
                    }}
                >
                    Send
                </Button>
            </div>
        </div>
    );
};

export default SerialMonitor;
